import React, { useState } from 'react';
import { IPurchaseOrder } from 'interfaces/purchaseOrder';
import OrderItems from './orderItems';
import { createPurchaseOrder } from 'services';
import styles from './index.module.scss';
import _ from 'lodash';
import { showError, showSuccess } from 'utils/notifications';
import ActionButton from 'components/ActionButton';
import ChangeSupplierConfirmationModal from 'components/ChangeSupplierConfirmationModal';
import SupplierAutosuggest from 'components/SupplierAutosuggest';
import Currency from 'components/Currency/Currency';
import useConfirmation from 'hooks/useConfirmation';

export const getEmptyItem = () => ({
  id: '',
  name: '',
  description: '',
  cost: 0,
  price: 0,
  quantity: 1,
  producerUnitMapping: '-',
  supplierUnitMapping: '-',
});

const CreatePurchaseOrder = props => {
  const [items, setItems] = useState<IPurchaseOrder.IOrderItemType[]>
  ([getEmptyItem()]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [temporarySupplierId, setTemporarySupplierId] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<Record<string, any>>({});
  const [temporarySupplier, setTemporarySupplier] = useState<Record<string, any>>({});
  const [orderTitle, setOrderTitle] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [confirmSupplierChangeModalOpen, setConfirmSupplierChangeModalOpen] = useState<boolean>(false);
  const [supplierName, setSupplierName] = useState<string>('');

  useConfirmation('Are you sure you want to leave this page?');

  const unblockNavigation = () => {
    const unblock = props.history.block();
    unblock();
  };

  const createPurchaseOrderFn = async () => {
    setLoading(true);
    const data = {
      title: orderTitle,
      inventoryItems: items.map(item => _.omit(item, ['name'])),
      supplierId: selectedSupplierId,
    };

    try {
      const res = await createPurchaseOrder(data);
      unblockNavigation();
      props.history.replace(`/purchase-orders/${res.id}`);
      showSuccess('Purchase order added successfully.');
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const getValidItems = () => {
    const itemArr = items.filter(item => item.id.length);
    return itemArr;
  };

  const getTotalQuantity = () => {
    return _.sumBy(getValidItems(), (record) => record.quantity || 0);
  };

  const getTotalPrice = () => {
    let total = 0;
    getValidItems().map(item => {
      total += (item.cost || 0) * (item.quantity || 0);
    });
    return parseFloat(total.toFixed(2));
  };

  const handleSupplierSelect = (supplierDetails: Record<string, any>) => {
    if (!selectedSupplierId) {
      setSelectedSupplier(supplierDetails);
      setSelectedSupplierId(supplierDetails.id);
    } else {
      setTemporarySupplier(supplierDetails);
      setTemporarySupplierId(supplierDetails.id);
      if (supplierDetails.id !== selectedSupplierId) {
        setConfirmSupplierChangeModalOpen(true);
      }
    }
  };

  const handleSupplierChange = () => {
    setSupplierName(temporarySupplier.full_name);
    setSelectedSupplierId(temporarySupplierId);
    setSelectedSupplier(temporarySupplier);
    setItems([getEmptyItem()]);
    setConfirmSupplierChangeModalOpen(false);
  };

  const cancelSupplierChange = () => {
    setSupplierName(selectedSupplier.full_name);
    setSelectedSupplierId(selectedSupplierId);
    setSelectedSupplier(selectedSupplier);
    setConfirmSupplierChangeModalOpen(false);
  };

  const renderSupplierSchedule = () => {
    const keys = Object.keys(selectedSupplier.schedule);

    return (
      <div className={styles['schedule-container']}>
        <div className={styles['label']}>Supplier Schedule</div>
        <div className={styles['days-container']}>
          {keys.map((day, i) => (
            <div key={i} className={styles['single-day']}>
              <input
                type={'checkbox'}
                checked={selectedSupplier.schedule[day]}
                onChange={() => { }}
              />
              <div className={styles['day-name']}>{day}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getFooter = () => (
    <div className={styles['full-footer-container']}>
      <div className={styles['footer-container']}>
        <div className={styles['footer']}>
          <div className={styles['supplier-name']}>
            <div className={styles['label']}>Supplier Name</div>
            <div className={styles['name']}>{selectedSupplier.displayName || '-'}</div>
          </div>
          <div className={styles['total-price']}>
            <div className={styles['label']}>Total Price</div>
            <div className={styles['price']}><Currency amount={getTotalPrice()} /></div>
          </div>
          <div className={styles['total-items']}>
            <div className={styles['label']}>Total Items</div>
            <div className={styles['items']}>{getValidItems().length}</div>
          </div>
          <div className={styles['total-quantity']}>
            <div className={styles['label']}>Total Quantity</div>
            <div className={styles['quantity']}>{getTotalQuantity()}</div>
          </div>
          <div className={styles['po-btn-for-desktop']}>
            <div className={styles['create-po-button']}>
              <ActionButton
                text={'Cancel'}
                active={!loading}
                isLoading={false}
                onClick={() => props.history.goBack()}
                customClass={styles['cancel']}
              />
              <ActionButton
                active={!loading}
                isLoading={loading}
                onClick={createPurchaseOrderFn}
                customClass={'form-control'}
                text={'Create Purchase Order'}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={styles['po-btn-for-mobile']}>
        <div className={'d-flex mx-0 my-3 justify-content-between'}>
          <ActionButton
            text={'Cancel'}
            active={!loading}
            isLoading={false}
            onClick={() => props.history.goBack()}
            customClass={styles['cancel']}
          />
          <ActionButton
            active={!loading}
            isLoading={loading}
            onClick={createPurchaseOrderFn}
            customClass={'form-control m-0'}
            text={'Create Purchase Order'}
          />
        </div>
      </div>
    </div>
  );

  const getBody = () => {
    const getItems = () => (
      <OrderItems
        disabled={loading}
        items={items}
        setItems={setItems}
        selectedSupplier={selectedSupplierId}
        showAddInventoryItemButton={!!selectedSupplierId}
      />
    );

    const getSupplier = () => (
      <div className={styles['supplier-container']}>
        <div className={styles['label']}>Supplier</div>
        <SupplierAutosuggest
          supplierName={supplierName}
          setSupplierName={setSupplierName}
          setSupplier={handleSupplierSelect}
          setSupplierId={() => { }}
          withSchedule={true}
        />
        {(selectedSupplierId && selectedSupplier.schedule) && renderSupplierSchedule()}
      </div>
    );

    const getTitle = () => (
      <div className={styles['title-container']}>
        <div className={styles['label']}>Order Title</div>
        <input
          disabled={loading}
          value={orderTitle}
          className='form-control w-25'
          onChange={e => setOrderTitle(e.target.value)}
        />
      </div>

    );

    return (
      <div className={styles['po-details-container']}>
        {getTitle()}
        {getSupplier()}
        {getItems()}
      </div>
    );
  };

  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Create a New Purchase Order</div>
    </div>
  );

  return (
    <>
      {confirmSupplierChangeModalOpen && (
        <ChangeSupplierConfirmationModal
          toggleModal={() => setConfirmSupplierChangeModalOpen(!confirmSupplierChangeModalOpen)}
          cancelChange={cancelSupplierChange}
          changeSupplier={handleSupplierChange}
        />
      )}
      <div className={`${styles['po-main-container']}`}>
        {getHeader()}
        {getBody()}
      </div>
      {getFooter()}
    </>
  );
};

export { CreatePurchaseOrder };
